import { useState, useMemo } from "react";
import { toast } from "sonner";
import { Check, Plus, ArrowRight, ArrowLeft, Loader2, X, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TOPICS, mediaName, FREQUENCIES, MIN_MEDIA } from "@/lib/onboardingTopics";

const norm = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

// Shared two-step onboarding flow (topics -> suggested media + custom).
// `onComplete(domains)` must be async and throw on failure. The page owns the header/banner.
export function OnboardingFlow({ username, onComplete }) {
  const [step, setStep] = useState(1);
  const [topics, setTopics] = useState([]); // selected topic keys
  const [selected, setSelected] = useState(() => new Set()); // selected media domains
  const [custom, setCustom] = useState([]); // [{name, domain}]
  const [customInput, setCustomInput] = useState("");
  const [frequency, setFrequency] = useState("twice_week");
  const [submitting, setSubmitting] = useState(false);

  const activeTopics = useMemo(() => TOPICS.filter((t) => topics.includes(t.key)), [topics]);
  const totalSelected = selected.size + custom.length;

  // Full media catalog (all topics) for search-by-name, deduped by domain.
  const catalog = useMemo(() => {
    const seen = new Set();
    const arr = [];
    TOPICS.forEach((t) =>
      t.media.forEach((m) => {
        if (!seen.has(m.domain)) {
          seen.add(m.domain);
          arr.push({ ...m, topicKey: t.key, topicLabel: t.label });
        }
      })
    );
    return arr;
  }, []);

  const activeDomains = useMemo(
    () => new Set(activeTopics.flatMap((t) => t.media.map((m) => m.domain))),
    [activeTopics]
  );
  const chosen = useMemo(
    () => new Set([...selected, ...custom.map((c) => c.domain)]),
    [selected, custom]
  );

  const query = customInput.trim();
  const matches = useMemo(() => {
    if (query.length < 2) return [];
    const q = norm(query);
    return catalog
      .filter((m) => !chosen.has(m.domain) && (norm(m.name).includes(q) || m.domain.includes(query.toLowerCase())))
      .slice(0, 6);
  }, [query, catalog, chosen]);

  const looksLikeDomain = query.includes(".") && !query.includes(" ") && !/\//.test(query.replace(/^https?:\/\//, ""));
  const cleanDomain = query.replace(/^https?:\/\//, "").replace(/\/+$/, "").toLowerCase();
  const showDomainOption =
    looksLikeDomain && !chosen.has(cleanDomain) && !catalog.some((m) => m.domain === cleanDomain);

  const toggleTopic = (key) => {
    setTopics((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const goToMedia = () => {
    if (topics.length === 0) {
      toast.error("Elige al menos un tema.");
      return;
    }
    // Pre-select all suggested media from the chosen topics.
    const next = new Set();
    TOPICS.filter((t) => topics.includes(t.key)).forEach((t) =>
      t.media.forEach((m) => next.add(m.domain))
    );
    setSelected(next);
    setStep(2);
  };

  const toggleMedia = (domain) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(domain)) next.delete(domain);
      else next.add(domain);
      return next;
    });
  };

  const addCustom = (e) => {
    e.preventDefault();
    if (matches.length > 0) {
      addFromCatalog(matches[0]);
      return;
    }
    if (showDomainOption) {
      addRawDomain(cleanDomain);
      return;
    }
    if (!query) return;
    if (!query.includes(".")) {
      toast.error("No encontramos ese medio. Prueba con un dominio, ej. ejemplo.com");
    } else {
      toast.error("Añade el dominio principal (sin secciones), ej. ejemplo.com");
    }
  };

  const addFromCatalog = (m) => {
    if (activeDomains.has(m.domain)) {
      setSelected((prev) => new Set(prev).add(m.domain));
    } else {
      setCustom((prev) => [...prev, { name: m.name, domain: m.domain }]);
    }
    setCustomInput("");
  };

  const addRawDomain = (d) => {
    if (d.includes("/")) {
      toast.error("Añade el dominio principal (sin secciones), ej. ejemplo.com");
      return;
    }
    setCustom((prev) => [...prev, { name: mediaName(d), domain: d }]);
    setCustomInput("");
  };

  const removeCustom = (domain) => setCustom((prev) => prev.filter((c) => c.domain !== domain));

  const goToFrequency = () => {
    if (totalSelected < MIN_MEDIA) {
      toast.error(`Añade al menos ${MIN_MEDIA} medios para continuar.`);
      return;
    }
    setStep(3);
  };

  const finish = async () => {
    const domains = [...selected, ...custom.map((c) => c.domain)];
    if (domains.length === 0) {
      toast.error("Selecciona al menos un medio.");
      return;
    }
    setSubmitting(true);
    try {
      await onComplete(domains, frequency);
    } catch {
      /* onComplete handles its own error toast */
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14" data-testid="onboarding">
      {/* Progress */}
      <div className="flex items-center gap-2 sm:gap-3 mb-8" data-testid="onboarding-progress">
        {[{ n: 1, l: "Temas" }, { n: 2, l: "Medios" }, { n: 3, l: "Frecuencia" }].map((s, i) => (
          <div key={s.n} className="flex items-center gap-2 sm:gap-3 flex-1 last:flex-none">
            <span className={`text-xs font-bold uppercase tracking-widest whitespace-nowrap ${step >= s.n ? "text-primary" : "text-muted-foreground"}`}>
              {s.n} · {s.l}
            </span>
            {i < 2 && (
              <div className="flex-1 h-px bg-border relative min-w-[16px]">
                <div className={`absolute inset-y-0 left-0 bg-primary transition-all duration-300 ${step > s.n ? "w-full" : "w-0"}`} />
              </div>
            )}
          </div>
        ))}
      </div>

      {step === 1 && (
        <div className="animate-in fade-in slide-in-from-right-4 duration-300">
          <p className="text-sm uppercase tracking-widest font-semibold text-primary mb-3">
            Hola, {username} 👋
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tight leading-[1.05]">
            ¿Qué temas te interesan?
          </h1>
          <p className="text-muted-foreground mt-4 max-w-xl">
            Elige uno o varios. Te sugeriremos los mejores medios de cada tema y podrás ajustarlos después.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-8" data-testid="topic-grid">
            {TOPICS.map((t) => {
              const active = topics.includes(t.key);
              const Icon = t.icon;
              return (
                <button
                  key={t.key}
                  data-testid={`topic-${t.key}`}
                  onClick={() => toggleTopic(t.key)}
                  className={`group relative flex items-center gap-4 p-5 border text-left transition-all duration-200 ${
                    active ? "border-primary bg-primary/5" : "border-border hover:border-foreground"
                  }`}
                >
                  <div className={`w-11 h-11 flex items-center justify-center shrink-0 transition-colors ${active ? "bg-primary text-white" : "bg-muted text-foreground"}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-heading font-black tracking-tight">{t.label}</p>
                    <p className="text-xs text-muted-foreground">{t.media.length} medios sugeridos</p>
                  </div>
                  <div className={`w-6 h-6 border flex items-center justify-center shrink-0 transition-colors ${active ? "bg-primary border-primary" : "border-border"}`}>
                    {active && <Check className="w-4 h-4 text-white" />}
                  </div>
                </button>
              );
            })}
          </div>

          <Button
            onClick={goToMedia}
            disabled={topics.length === 0}
            data-testid="onboarding-continue-button"
            className="rounded-none bg-primary hover:bg-blue-900 h-12 mt-8 gap-2 px-8"
          >
            Continuar
            <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      )}

      {step === 2 && (
        <div className="animate-in fade-in slide-in-from-right-4 duration-300 pb-44 sm:pb-28">
          <button
            onClick={() => setStep(1)}
            data-testid="onboarding-back-button"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4" /> Cambiar temas
          </button>
          <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tight leading-[1.05]">
            Medios recomendados
          </h1>
          <p className="text-muted-foreground mt-4 max-w-xl">
            Vienen pre-seleccionados. Quita los que no quieras o añade otros medios que sigas.
          </p>

          <div className="mt-8 space-y-8">
            {activeTopics.map((t) => (
              <section key={t.key} data-testid={`media-group-${t.key}`}>
                <div className="flex items-center gap-2 mb-3">
                  <t.icon className="w-4 h-4 text-primary" />
                  <h2 className="text-xs uppercase tracking-widest font-bold text-muted-foreground">{t.label}</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {t.media.map((m) => {
                    const active = selected.has(m.domain);
                    return (
                      <button
                        key={m.domain}
                        data-testid={`media-${m.domain}`}
                        onClick={() => toggleMedia(m.domain)}
                        className={`flex items-center gap-3 p-3.5 border text-left transition-all duration-200 ${
                          active ? "border-primary bg-primary/5" : "border-border hover:border-foreground opacity-70 hover:opacity-100"
                        }`}
                      >
                        <div className={`w-5 h-5 border flex items-center justify-center shrink-0 transition-colors ${active ? "bg-primary border-primary" : "border-border"}`}>
                          {active && <Check className="w-3.5 h-3.5 text-white" />}
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-sm truncate">{m.name}</p>
                          <p className="text-xs text-muted-foreground truncate">{m.domain}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </section>
            ))}

            {/* Search & add media by name */}
            <section data-testid="custom-media-section">
              <div className="flex items-center gap-2 mb-3">
                <Search className="w-4 h-4 text-primary" />
                <h2 className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Buscar y añadir medios</h2>
              </div>
              <div className="relative">
                <form onSubmit={addCustom} className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                    <Input
                      data-testid="media-search-input"
                      placeholder="Busca por nombre (ej. Marca) o dominio (ej. expansion.com)"
                      value={customInput}
                      onChange={(e) => setCustomInput(e.target.value)}
                      className="rounded-none border-border h-11 pl-9"
                    />
                  </div>
                  <Button type="submit" data-testid="custom-domain-add" variant="outline" className="rounded-none border-border h-11 px-4 gap-2 shrink-0">
                    <Plus className="w-4 h-4" /> Añadir
                  </Button>
                </form>

                {(matches.length > 0 || showDomainOption) && (
                  <div className="absolute z-20 left-0 right-0 mt-1 bg-background border border-border shadow-xl max-h-72 overflow-auto" data-testid="media-search-results">
                    {matches.map((m) => (
                      <button
                        key={m.domain}
                        type="button"
                        data-testid={`media-search-result-${m.domain}`}
                        onClick={() => addFromCatalog(m)}
                        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-muted/60 border-b border-border last:border-0 transition-colors"
                      >
                        <span className="min-w-0">
                          <span className="block font-semibold text-sm truncate">{m.name}</span>
                          <span className="block text-xs text-muted-foreground truncate">{m.domain}</span>
                        </span>
                        <span className="text-[10px] uppercase tracking-wide bg-muted px-2 py-0.5 shrink-0 text-muted-foreground">{m.topicLabel}</span>
                      </button>
                    ))}
                    {showDomainOption && (
                      <button
                        type="button"
                        data-testid="media-search-add-domain"
                        onClick={() => addRawDomain(cleanDomain)}
                        className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-muted/60 text-primary font-semibold text-sm transition-colors"
                      >
                        <Plus className="w-4 h-4" /> Añadir dominio “{cleanDomain}”
                      </button>
                    )}
                  </div>
                )}
              </div>

              {custom.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3" data-testid="custom-media-list">
                  {custom.map((c) => (
                    <span key={c.domain} className="inline-flex items-center gap-2 bg-primary/10 text-primary text-sm font-semibold px-3 py-1.5 border border-primary/30">
                      {c.name}
                      <button onClick={() => removeCustom(c.domain)} data-testid={`custom-remove-${c.domain}`} aria-label={`Quitar ${c.name}`} className="hover:text-blue-900">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </section>
          </div>

          {/* Sticky footer: value bar (min 5 for newsletter variety) */}
          <div className="fixed bottom-0 inset-x-0 bg-background/95 backdrop-blur border-t border-border z-50">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
              <div className="flex-1 min-w-0" data-testid="value-bar">
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-sm font-semibold" data-testid="selected-count">
                    <span className="text-primary font-black">{totalSelected}</span> / {MIN_MEDIA} medios
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {totalSelected >= MIN_MEDIA
                      ? "¡Perfecto! Tendrás variedad de sobra en tu resumen."
                      : `Añade ${MIN_MEDIA - totalSelected} más para no repetir noticias en tu resumen.`}
                  </p>
                </div>
                <div className="h-2 bg-muted overflow-hidden">
                  <div
                    className={`h-full transition-all duration-300 ${totalSelected >= MIN_MEDIA ? "bg-green-500" : "bg-primary"}`}
                    style={{ width: `${Math.min(100, (totalSelected / MIN_MEDIA) * 100)}%` }}
                  />
                </div>
              </div>
              <Button
                onClick={goToFrequency}
                disabled={totalSelected < MIN_MEDIA}
                data-testid="onboarding-continue-media-button"
                className="rounded-none bg-primary hover:bg-blue-900 h-12 gap-2 px-6 sm:px-8 shrink-0"
              >
                Continuar
                <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="animate-in fade-in slide-in-from-right-4 duration-300" data-testid="frequency-step">
          <button
            onClick={() => setStep(2)}
            data-testid="onboarding-back-media-button"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4" /> Volver a medios
          </button>
          <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tight leading-[1.05]">
            ¿Cada cuánto quieres tu resumen?
          </h1>
          <p className="text-muted-foreground mt-4 max-w-xl">
            Te enviaremos por email las últimas noticias resumidas de tus medios. Podrás cambiarlo cuando quieras en Ajustes.
          </p>

          <div className="mt-8 space-y-3" data-testid="frequency-list">
            {FREQUENCIES.map((f) => {
              const active = frequency === f.key;
              return (
                <button
                  key={f.key}
                  data-testid={`frequency-${f.key}`}
                  onClick={() => setFrequency(f.key)}
                  className={`w-full flex items-center gap-4 p-5 border text-left transition-all duration-200 ${
                    active ? "border-primary bg-primary/5" : "border-border hover:border-foreground"
                  }`}
                >
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${active ? "border-primary" : "border-border"}`}>
                    {active && <div className="w-2.5 h-2.5 rounded-full bg-primary" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-heading font-black tracking-tight flex items-center gap-2 flex-wrap">
                      {f.label}
                      {f.recommended && (
                        <span className="text-[10px] uppercase tracking-wide bg-primary text-white px-2 py-0.5">Recomendado</span>
                      )}
                    </p>
                    <p className="text-sm text-muted-foreground">{f.desc}</p>
                  </div>
                </button>
              );
            })}
          </div>

          <Button
            onClick={finish}
            disabled={submitting}
            data-testid="onboarding-start-button"
            className="rounded-none bg-primary hover:bg-blue-900 h-12 mt-8 gap-2 px-8"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {submitting ? "Preparando tu feed…" : "Comenzar a analizar"}
            {!submitting && <ArrowRight className="w-4 h-4" />}
          </Button>
        </div>
      )}
    </main>
  );
}
